import { Component, OnInit, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Department, Role, ActionIn, InsightConfigurationType, Configuration } from 'src/app/core/models/insight-configuration.model';
import { Store } from '@ngxs/store';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { GetSubTypes, UpdateStepValidity, SetSelectedConfiguration } from 'src/app/core/store/predictive-failure-configuration/predictive-failure-configuration.action';
import { PREDICTIVE_CREATE_COMPONENTS, PREDICTIVE_CONFIG_DROPDOWN_FIELDS, FREQUENCY_VALUE_MAX, UOM_MEASUREMENT_TYPE } from 'src/app/core/enums/predictiveFailure.enum';
import { CONFIG_MODE, CONFIG_TYPE} from 'src/app/core/enums/insightConfigurations.enum';
import { UnitOfMeasureService } from 'src/app/core/services/unit-of-measure/unit-of-measure.service';
import _ from 'lodash';
@Component({
  selector: 'app-predictive-configuration',
  templateUrl: './predictive-configuration.component.html',
  styleUrls: ['./predictive-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class PredictiveConfigurationComponent implements OnInit {
  @Output() configDetailsChecker = new EventEmitter<any>();
  public themeList: any[] = []
  public departmentList: Department[] = [];
  public configStatus; //status of the config details
  public dropdownFieldEnum = PREDICTIVE_CONFIG_DROPDOWN_FIELDS;
  public roleListByPlant: Role[] = [];
  public roleListByDepartment: Role[] = [];
  public roleList: Role[] = [];
  public priorityList = ['High', 'Medium', 'Low'];
  public frequencyList = ['Hour', 'Day', 'Minute'];
  public thresholdList = ['Equal', 'Greater than','Less than'];
  public subTypeList = [];
  public unsubscribe = new Subject<void>();
  public actionList = [];
  public configurationTypes : InsightConfigurationType[];
  public actionInList: ActionIn[] = [
  {value: 1, unit: 'Day'},
  {value: 2, unit: 'Day'},
  {value: 3, unit: 'Day'},
  {value: 4, unit: 'Day'},
  {value: 5, unit: 'Day'},
  {value: 6, unit: 'Day'},
  {value: 1, unit: 'Week'},
  {value: 2, unit: 'Week'},
  {value: 3, unit: 'Week'},
  {value: 1, unit: 'Month'}
  ];
  public generalValidity;
  public customValidity;
  public show: boolean = false;

  public predictiveConfigForm: UntypedFormGroup;
  public predictiveCustomConfigForm: UntypedFormGroup;
  public dropdownFieldShow = {
    department: false,
    role: false,
    status: false,
    priority : false,
    theme: false,
    subtype: false,
    actionIn: false,
    frequency: false,
    thresholdCondition: false,
  }

  public mode = CONFIG_MODE.CREATE

  public uomDefault = this.store.selectSnapshot(state => state.configurations.predictiveFailure.uomToDisplay)
  public thresholdValueOptions = {
    align:'center',
    allowNegative: true,
    prefix: '',
    suffix: ``,
    precision: 3
  }
  public thresholdPlaceholder = `0${this.thresholdValueOptions.suffix}`;
  public thresholdRange = {
    min: 1,
    max: 99
  }

  public numberFormat =  '1.0-0';

  constructor(
    private store: Store,
    private uomService: UnitOfMeasureService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.mode = this.store.selectSnapshot(state => state.configurations.predictiveFailure.mode);
    this.actionInList = this.actionInList.map(actionIn => {
      actionIn.dataToShow = `${actionIn.value} ${actionIn.unit}${actionIn.value > 1 ? 's': ''}`
      return actionIn
    })
    this.getDepartments();
    this.getThemeList();
    this.initForm();
    this.preSaveDetails();

    let category = this.store.selectSnapshot(state => state.configurations.selectedConfigType);
    if(this.mode === CONFIG_MODE.CREATE){
      if(category.viewId !== CONFIG_TYPE.PREDICTIVE_FAILURE){
        this.show = true;
      }
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public initForm() {
    this.getThresholdUomConfigDisplay();

    this.configStatus = this.store.selectSnapshot(state => state.configurations.predictiveFailure.configurationDetails);
    const data = this.prepEditFormValues(this.configStatus);

    this.predictiveConfigForm = new UntypedFormGroup({
      department: new UntypedFormControl( data.department ? data.department : null, [Validators.required]),
      role: new UntypedFormControl( data.role ? data.role : {value: null, disabled: (!this.configStatus.department)}, [Validators.required]),
      priority: new UntypedFormControl( data.priority ? data.priority : null, [Validators.required]),
      theme: new UntypedFormControl( data.theme ? data.theme : null, [Validators.required]),
      subtype: new UntypedFormControl( data.subtype ? data.subtype : {value: null, disabled: (!this.configStatus.theme)}, [Validators.required]),
      description: new UntypedFormControl( data.description ? data.description : null, [Validators.required, Validators.minLength(10), Validators.maxLength(500)]),
      actionIn: new UntypedFormControl( data.actionIn ? data.actionIn : null, [Validators.required]),
    })

    this.predictiveCustomConfigForm = new UntypedFormGroup({
      frequencyUnit: new UntypedFormControl( data.frequencyUnit ? data.frequencyUnit : null, [Validators.required]),
      frequencyValue: new UntypedFormControl( data.frequencyValue ? data.frequencyValue: null, [Validators.required]),
      thresholdCondition: new UntypedFormControl( data.thresholdCondition ? data.thresholdCondition : null, [Validators.required]),
      thresholdValue: new UntypedFormControl( data.thresholdValue ? data.thresholdValue : null, [Validators.required, Validators.min(this.thresholdRange.min), Validators.max(this.thresholdRange.max)]),
    })

    if(this.configStatus.theme != null) {
      this.getSubTypes();
    }
    if(this.configStatus.department != null) {
      this.getRolesByDepartment();
    } else {
      this.predictiveConfigForm.controls.role?.disable();
      this.predictiveConfigForm.controls.role?.updateValueAndValidity();
    }
    // Add addtional validators for frequencyValue depending on frequencyUnit value
    this.predictiveCustomConfigForm.controls.frequencyValue.setValidators([Validators.required, Validators.min(1), Validators.max(FREQUENCY_VALUE_MAX[data.frequencyUnit])])
    this.predictiveCustomConfigForm.controls.frequencyValue.updateValueAndValidity();
    this.predictiveCustomConfigForm.updateValueAndValidity();
    //set the validity status of forms when back button is clicked
    this.generalValidity = this.predictiveConfigForm.valid;
    this.customValidity = this.predictiveCustomConfigForm.valid;

    this.store.dispatch(new UpdateStepValidity(PREDICTIVE_CREATE_COMPONENTS.CONFIGURATIONS, this.predictiveConfigForm.valid))
    this.store.dispatch(new UpdateStepValidity(PREDICTIVE_CREATE_COMPONENTS.CONFIGURATIONS, this.predictiveCustomConfigForm.valid))
    //checks the form validity on init form
    this.validityChecker();
    this.saveDetails();
  }

  public getThresholdUomConfigDisplay() {
    const thresholdUomConfig = this.store.selectSnapshot(state => state.configurations.predictiveFailure.thresholdUomConfig);

    let decimalPlace = UOM_MEASUREMENT_TYPE.PERCENTAGE === thresholdUomConfig?.type ? 0: 3;
    this.numberFormat = `1.${decimalPlace}-${decimalPlace}`;
    let uomToDisplay = this.uomService.getUoMPerType(thresholdUomConfig?.type, this.store.selectSnapshot(state => state.unitOfMeasure.userDefaultUoMs));
    const uomStandard = this.uomService.getUoMPerType(thresholdUomConfig?.type, this.store.selectSnapshot(state => state.unitOfMeasure.standardUoMs));

    if(!uomToDisplay) {
      uomToDisplay = {...uomStandard}
    }
    this.thresholdValueOptions.suffix = ` ${uomToDisplay?.Symbol}`;
    this.thresholdValueOptions.precision = decimalPlace;
    this.thresholdPlaceholder = `0${decimalPlace ? '.'+'0'.repeat(this.thresholdValueOptions?.precision) : ''} ${this.thresholdValueOptions?.suffix}`;

    // Get Min Max Value
    const conversionFormulaFromStandardToUIDefault = this.uomService.getUoMConversionFormula(uomStandard?.UoMId, uomToDisplay?.UoMId, this.store.selectSnapshot(state => state.unitOfMeasure.uomConversions));

    this.thresholdRange.min =  _.round(this.uomService.calculateUoM(thresholdUomConfig?.min, conversionFormulaFromStandardToUIDefault), decimalPlace);
    this.thresholdRange.max = _.round(this.uomService.calculateUoM(thresholdUomConfig?.max, conversionFormulaFromStandardToUIDefault), decimalPlace);
  }

  public saveDetails() {
    // Listens to form status changes and update the state accordingly
    this.predictiveConfigForm.statusChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      this.generalValidity = value;
      this.validityChecker();
    })

    this.predictiveCustomConfigForm.statusChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      this.customValidity = value;
      this.validityChecker();
    })
  }

  public validityChecker(){
    // converts the value to boolean
    this.generalValidity = !!(this.generalValidity === 'VALID' || this.generalValidity === true);
    this.customValidity = !!(this.customValidity === 'VALID' || this.customValidity === true);

    if(this.generalValidity && this.customValidity) {
      this.store.dispatch(new UpdateStepValidity(PREDICTIVE_CREATE_COMPONENTS.CONFIGURATIONS, true))
    }else{
      this.store.dispatch(new UpdateStepValidity(PREDICTIVE_CREATE_COMPONENTS.CONFIGURATIONS, false))
    }
  }

  public preSaveDetails() {
    // listens to form value updates and store it to state
    this.predictiveConfigForm.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      this.createPredictiveConfig();
    })

    this.predictiveCustomConfigForm.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      this.createPredictiveConfig();
    })
  }

  public prepEditFormValues(config: Configuration) {
    if(config.category === CONFIG_TYPE.CUSTOM_PREDICTIVE && this.mode === CONFIG_MODE.EDIT){
      this.show = true;
    }
    // Parse values from saved from state to be populated to angular form
    let department = config.department;
    let role = config.role;
    const priority = this.priorityList.find(priority => priority.toUpperCase() === config.priority?.toUpperCase())
    const theme = config.theme;
    const subtype = config.subtype;
    const actionIn = this.actionInList.find(actionIn => config.actionIn?.value === actionIn.value && config.actionIn?.unit === actionIn.unit);
    const description = config.description;

    // Add custom values here:
    const frequencyValue = config.frequencyValue;
    const frequencyUnit = config.frequencyUnit;
    const thresholdCondition = config.thresholdCondition;
    const thresholdValue = config.thresholdValue;

    if(!department?.departmentId) {
      department = null;
    }

    if(!role?.roleId) {
      role = null;
    }

    return {department, role, priority, theme, actionIn, subtype, description ,thresholdCondition, thresholdValue, frequencyValue, frequencyUnit}
  }

  public getThemeList() {
    this.store.select(state => state.configurations.predictiveFailure.themeList).pipe(takeUntil(this.unsubscribe))
    .subscribe(themeList => {
      this.themeList = themeList
    })
  }
  public getDepartments() {
    this.store.select(state => state.configurations.departmentList).pipe(takeUntil(this.unsubscribe))
    .subscribe(departmentList => {
      this.departmentList = departmentList
    })
  }
  public getRolesByDepartment() {
    let department = this.predictiveConfigForm.value.department;
    let departmentRoleList = this.store.selectSnapshot(state => state.configurations.departmentRoleList);
    let deptRole =departmentRoleList.find(deptRole => deptRole.departmentId === department.departmentId);

    this.roleList = deptRole? deptRole.roles: [];
  }

  public getSubTypes(): void {
    const theme = this.predictiveConfigForm.value.theme;
    let configId= '';
    if(this.mode === CONFIG_MODE.EDIT){
      configId = this.store.selectSnapshot(state => state.configurations.predictiveFailure.configurationDetailsSaved?.categoryId);
    }else{
      configId = this.store.selectSnapshot(state => state.configurations.selectedConfigType?.viewId);
    }

    this.store.dispatch(new GetSubTypes(theme.id, configId)).pipe(takeUntil(this.unsubscribe))
    .subscribe((state) => {
      this.subTypeList= state.configurations.predictiveFailure.subTypeList;
      this.cdr.detectChanges();
    })
  }

  public showDropdownMenu(fieldName: string) {
    this.dropdownFieldShow[fieldName] = !this.dropdownFieldShow[fieldName];
  }
  public onClickedOutside(fieldName: string) {
    this.dropdownFieldShow[fieldName] = false;
  }

  public selectOption(data:{dropdownName: string, option: string}) {
    this.predictiveConfigForm.controls[data.dropdownName].setValue(data.option)

    switch(data.dropdownName) {
      case this.dropdownFieldEnum.DEPARTMENT:
        this.roleList = [];
        this.getRolesByDepartment();
        this.predictiveConfigForm.controls.role?.setValue(null);
        this.predictiveConfigForm.controls.role?.enable();
        this.predictiveConfigForm.controls.role?.updateValueAndValidity();
        break;
      case this.dropdownFieldEnum.THEME:
        this.subTypeList= [];
        this.getSubTypes();
        this.predictiveConfigForm.controls.subtype.setValue(null);
        this.predictiveConfigForm.controls.subtype.enable();
        this.predictiveConfigForm.controls.subtype.updateValueAndValidity();
        break;
    }

    this.dropdownFieldShow[data.dropdownName] = false;
  }

  public selectCustomOption(data:{dropdownName: string, option: string}) {
    this.predictiveCustomConfigForm.controls[data.dropdownName].setValue(data.option)

    if(data.dropdownName == this.dropdownFieldEnum.FREQUENCY) {
        this.predictiveCustomConfigForm.controls.frequencyValue.enable();
        this.predictiveCustomConfigForm.controls.frequencyValue.setValue(null);
        this.predictiveCustomConfigForm.controls.frequencyValue.setValidators([Validators.required, Validators.min(1), Validators.max(FREQUENCY_VALUE_MAX[data.option])]);
        this.predictiveCustomConfigForm.controls.frequencyValue.updateValueAndValidity();
    }

    this.dropdownFieldShow[data.dropdownName] = false;
  }

  public createPredictiveConfig() {
    let generalPredictiveConfigBody = this.predictiveConfigForm.getRawValue();
    let customPredictiveConfigBody = this.predictiveCustomConfigForm.getRawValue();
    const predictiveConfigBody = Object.assign(generalPredictiveConfigBody, customPredictiveConfigBody)
    this.store.dispatch(new SetSelectedConfiguration(predictiveConfigBody))
  }

  get FREQUENCY_VALUE_MAX() {
    // expose enum to component template
    return FREQUENCY_VALUE_MAX;
  }
}
